import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Button from '../ui/Button/Button'
import ThemeToggle from '../ui/ThemeToggle/ThemeToggle'
import { CloseIcon, MenuIcon } from '../ui/Icon/Icon'
import { useScrollSpy } from '../../hooks/useScrollSpy'
import { useScrollPosition } from '../../hooks/useScrollPosition'
import { navLinks, site } from '../../data/portfolio'
import styles from './Navbar.module.css'

const Navbar = ({ sections }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const isScrolled = useScrollPosition(24)
  const panelRef = useRef(null)
  const toggleRef = useRef(null)

  const links = useMemo(
    () => navLinks.filter((link) => sections.includes(link.id)),
    [sections]
  )
  const sectionIds = useMemo(() => links.map((link) => link.id), [links])
  const activeId = useScrollSpy(sectionIds)

  const closeMenu = useCallback(() => setIsMenuOpen(false), [])

  useEffect(() => {
    if (!isMenuOpen) return undefined

    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    const behind = [document.getElementById('main'), document.querySelector('footer')]
    behind.forEach((element) => element?.setAttribute('inert', ''))

    return () => {
      document.body.style.overflow = overflow
      behind.forEach((element) => element?.removeAttribute('inert'))
    }
  }, [isMenuOpen])

  useEffect(() => {
    if (!isMenuOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return
      closeMenu()
      toggleRef.current?.focus()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isMenuOpen, closeMenu])

  useEffect(() => {
    if (!isMenuOpen) return
    panelRef.current?.querySelector('a')?.focus()
  }, [isMenuOpen])

  useEffect(() => {
    const query = window.matchMedia('(min-width: 48em)')
    const handleChange = (event) => {
      if (event.matches) closeMenu()
    }

    query.addEventListener('change', handleChange)
    return () => query.removeEventListener('change', handleChange)
  }, [closeMenu])

  return (
    <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`.trim()}>
      <div className={`container ${styles.inner}`}>
        <a href="#top" className={styles.brand} aria-label={`${site.title}, back to top`}>
          <span className={styles.brandMark}>{site.initials}</span>
          <span className={styles.brandDot} aria-hidden="true" />
        </a>

        <nav className={styles.desktopNav} aria-label="Primary">
          <ul className={styles.navList}>
            {links.map((link) => (
              <li key={link.id}>
                <a
                  href={`#${link.id}`}
                  className={`${styles.navLink} ${
                    activeId === link.id ? styles.navLinkActive : ''
                  }`.trim()}
                  aria-current={activeId === link.id ? 'true' : undefined}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.actions}>
          <ThemeToggle />

          <Button
            ref={toggleRef}
            variant="icon"
            className={styles.menuButton}
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
          </Button>
        </div>
      </div>

      {/* Kept mounted so the transition can play in both directions. While
          closed it is hidden from assistive tech and removed from tab order. */}
      <div
        id="mobile-menu"
        ref={panelRef}
        className={`${styles.drawer} ${isMenuOpen ? styles.drawerOpen : ''}`.trim()}
        aria-hidden={!isMenuOpen}
      >
        <nav aria-label="Mobile">
          <ul className={styles.drawerList}>
            {links.map((link, index) => (
              <li key={link.id} style={{ transitionDelay: `${index * 45}ms` }}>
                <a
                  href={`#${link.id}`}
                  className={`${styles.drawerLink} ${
                    activeId === link.id ? styles.drawerLinkActive : ''
                  }`.trim()}
                  onClick={closeMenu}
                  tabIndex={isMenuOpen ? undefined : -1}
                >
                  <span className={styles.drawerIndex}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  )
}

export default Navbar
